'use client';
import { FileState, IFile } from '@/types';
import { Dispatch, FC, SetStateAction, useRef } from 'react';
import Contract from '../icons/Document/Contract';
import Download from '../icons/Navigation/Download';
import FileStatus from './FileStatus';
import styles from './styles.module.scss';

const DropZone: FC<{
  label?: string;
  subText: string;
  disabled?: boolean;
  multiple?: boolean;
  files: IFile[];
  accept: string[];
  maxLength: number;
  setFiles: Dispatch<SetStateAction<IFile[]>>;
}> = ({
  label,
  subText,
  disabled = false,
  multiple = false,
  files,
  accept,
  maxLength,
  setFiles,
}) => {
  const hiddenFileInput = useRef<HTMLInputElement>(null);

  function onClick() {
    if (!disabled) hiddenFileInput.current?.click();
  }

  function uploadFile(file: File) {
    const reader = new FileReader();
    reader.onloadstart = () => {
      setFiles([...files, { file, state: FileState.LOADING, error: '' }]);
    };
    reader.onload = () => {
      let state = FileState.DEFAULT;
      let error = '';
      if (file.size > maxLength) {
        state = FileState.ERROR;
        error = 'Le document a une taille trop élevée';
      }
      if (!accept.includes(file.type)) {
        state = FileState.ERROR;
        error = "Le document n'est pas dans le bon format";
      }
      setFiles((prevFiles) => {
        return prevFiles.map((updatedFile) => {
          if (file && updatedFile.file.name === file.name) {
            return { ...updatedFile, state, error };
          }
          return updatedFile;
        });
      });
    };
    reader.readAsDataURL(file);
  }

  function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files) {
      const droppedFiles = Array.from(e.target.files) as File[];
      const file = droppedFiles[0];
      uploadFile(file);
    }
  }

  function onFileDelete(name: string) {
    setFiles((prevFiles) => {
      return prevFiles.filter((updatedFile) => updatedFile.file.name !== name);
    });
  }
  function dropHandler(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.currentTarget.removeAttribute('drop-active');
    if (e.dataTransfer.items) {
      const file = e.dataTransfer.items[0].getAsFile();
      file && uploadFile(file);
    }
  }

  function dragOverHandler(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.currentTarget.setAttribute('drop-active', 'true');
  }
  function dragLeaveHandler(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.currentTarget.removeAttribute('drop-active');
  }

  return (
    <div className={`flex-column gap-s ${styles.container}`}>
      <div className="flex-column gap-xs">
        {label && (
          <div className="flex-row gap-s">
            <Contract className={styles.icon} />
            <h5>{label}</h5>
          </div>
        )}
        <p className="s">
          <pre>{subText}</pre>
        </p>
      </div>
      <div
        onDrop={(e) => dropHandler(e)}
        onDragOver={(e) => dragOverHandler(e)}
        onDragLeave={(e) => dragLeaveHandler(e)}
        onClick={onClick}
        className={`flex-column gap-s ${styles.drop} ${
          disabled || (!multiple && files.length >= 1)
            ? styles.disabled
            : styles.enabled
        }`}
      >
        <Download />
        <p className="s">
          Déposez le(s) document(s) dans cette zone
          <br />
          ou <u>importez un fichier</u>
        </p>
      </div>
      <input
        type="file"
        accept={accept.join(',')}
        onChange={onChange}
        ref={hiddenFileInput}
        value={''}
        style={{ display: 'none' }} // Make the file input element invisible
      />
      {files.map((file, index) => (
        <FileStatus key={index} file={file} onFileDelete={onFileDelete} />
      ))}
    </div>
  );
};

export default DropZone;
